import { useCallback, useEffect, useRef, useState } from "react";
import type { Editor } from "@tiptap/react";
import {
  DESKTOP_CHAPTER_COMMENT_TYPES,
  formatChapterCommentTypeLabel,
} from "../../../shared/chapterCommentLabels";
import { captureCommentSelection } from "../../utils/manuscriptEditorHelpers";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Textarea } from "../ui/textarea";

export type CapturedCommentSelection = {
  anchor: { from: number; to: number; excerpt?: string };
  blockId: string | null;
};

type ManuscriptCommentComposerProps = {
  editor: Editor | null;
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: {
    text: string;
    type: string;
    anchor: CapturedCommentSelection["anchor"];
    blockId: string | null;
  }) => void;
};

/**
 * In-document comment composer. Captures selection at open so focusing the
 * textarea does not lose the manuscript anchor (Word / Docs style).
 */
export function ManuscriptCommentComposer({
  editor,
  open,
  onClose,
  onSubmit,
}: ManuscriptCommentComposerProps) {
  const [captured, setCaptured] = useState<CapturedCommentSelection | null>(null);
  const [text, setText] = useState("");
  const [type, setType] = useState("note");
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (!open || !editor) {
      setCaptured(null);
      setText("");
      setType("note");
      return;
    }
    const next = captureCommentSelection(editor);
    if (!next) {
      onClose();
      return;
    }
    setCaptured(next);
    setText("");
    setType("note");
    window.setTimeout(() => inputRef.current?.focus(), 0);
  }, [open, editor, onClose]);

  const submit = useCallback(() => {
    if (!captured) return;
    const trimmed = text.trim();
    if (!trimmed) return;
    onSubmit({ text: trimmed, type, anchor: captured.anchor, blockId: captured.blockId });
    onClose();
  }, [captured, onClose, onSubmit, text, type]);

  return (
    <Dialog
      open={open && Boolean(editor) && Boolean(captured)}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="sm:max-w-md" showCloseButton>
        <DialogHeader>
          <DialogTitle>Add comment</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {captured?.anchor.excerpt ? (
            <p className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
              “{captured.anchor.excerpt}”
            </p>
          ) : null}
          <div className="space-y-1.5">
            <Label htmlFor="manuscript-comment-composer-type">Type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger id="manuscript-comment-composer-type" className="w-full" size="default">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DESKTOP_CHAPTER_COMMENT_TYPES.map((t) => (
                  <SelectItem key={t} value={t}>
                    {formatChapterCommentTypeLabel(t)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="manuscript-comment-composer-text">Comment</Label>
            <Textarea
              id="manuscript-comment-composer-text"
              ref={inputRef}
              aria-label="Comment text"
              placeholder="Add a comment…"
              rows={3}
              value={text}
              onChange={(event) => setText(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
                  event.preventDefault();
                  submit();
                }
              }}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!text.trim()} onClick={submit}>
            Add
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
